import { ReactNode, useContext, useEffect, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router";
import SpinnerArea from "../../../components/SpinnerArea";
import MainContext from "../../../context/mainContext";

import { StepModel } from "../../../models/step.model";
import {
  WorkflowModel,
  WorkflowParentInfo,
} from "../../../models/workflow.model";
import workflowService from "../../../services/workflow.service";
import WorkflowContext from "./WorkflowContext";

const _defaultSteps: StepModel[] = [
  //{ code: "preliminary-questions", isCompleted: false },
  {
    code: "general-information",
    isCompleted: false,
    approvalRequired: false,
    isApproved: false,
  },
  {
    code: "draft-for-feedback",
    isCompleted: false,
    approvalRequired: true,
    isApproved: false,
  },
  //{ code: "waiting-feedback", isCompleted: false },
  {
    code: "final-draft",
    isCompleted: false,
    approvalRequired: true,
    isApproved: false,
  },
  //{ code: "draft-approval", isCompleted: false },
  {
    code: "final-version",
    isCompleted: false,
    approvalRequired: true,
    isApproved: false,
  },
  //{ code: "final-approval", isCompleted: false },
  {
    code: "metadata",
    isCompleted: false,
    approvalRequired: true,
    isApproved: false,
  },
  {
    code: "published",
    isCompleted: false,
    approvalRequired: false,
    isApproved: false,
  },
];

const _defaultDeleteSteps: StepModel[] = [
  {
    code: "general-information-delete",
    isCompleted: false,
    approvalRequired: false,
    isApproved: false,
  },
  {
    code: "deleted",
    isCompleted: false,
    approvalRequired: false,
    isApproved: false,
  },
];

const WorkflowContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { service } = useContext(MainContext);
  const { ticketNumber } = useParams();
  const location = useLocation();

  const navigate = useNavigate();
  const [current, setCurrent] = useState<WorkflowModel | null | undefined>();

  const [loading, setLoading] = useState(false);

  const [trigger, setTrigger] = useState(false);

  const [defaultSteps, setDefaultSteps] = useState<StepModel[]>(
    location.pathname.indexOf("delete-publication") > -1
      ? _defaultDeleteSteps
      : _defaultSteps
  );

  const workflowParentInfo: WorkflowParentInfo | undefined =
    (location.pathname.indexOf("modify-publication") > -1 ||
      location.pathname.indexOf("delete-publication") > -1) &&
    location.state &&
    location.state.ticketNumber
      ? {
          ticketNumber: location.state.ticketNumber,
          workflowReason: location.state.workflowReason,
          workflowType: location.state.workflowType,
        }
      : undefined;

  useEffect(() => {
    let active = true;

    if (ticketNumber) {
      service(() => workflowService.getWorkflow(ticketNumber!), {
        callback: (response) => {
          if (active) setCurrent(response);
        },
        handleError: (error) => {
          if (active) {
            setCurrent(undefined);
            navigate(`${ticketNumber}/not-found`, { relative: "route" });
          }
        },
        loader: setLoading,
      });
    } else {
      setCurrent({
        steps: [...defaultSteps],
        activeStep: "general-information",
      } as WorkflowModel);
    }

    return () => {
      active = false;
    };
  }, [ticketNumber, trigger]);

  const [activeStepIndex, setActiveStepIndex] = useState(-1);

  useEffect(() => {
    if (current) {
      const i = current.steps.findIndex((x) => x.code === current.activeStep);
      setActiveStepIndex(i);
    }
  }, [current]);

  const reload = () => {
    setTrigger(!trigger);
  };

  return (
    <WorkflowContext.Provider
      value={{
        currentWorkflow: current,
        activeStep: activeStepIndex,
        steps: current?.steps || _defaultSteps,
        reload: reload,
        ticketNumber,
        workflowParentInfo,
      }}
    >
      <SpinnerArea loading={loading}>{!loading && children}</SpinnerArea>
    </WorkflowContext.Provider>
  );
};

export default WorkflowContextProvider;
